import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function AddPatientDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [gender, setGender] = useState("Male");
  const [ageType, setAgeType] = useState("YRS");
  const [ageValue, setAgeValue] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !ageValue) return toast.error("Name and age required");
    if (!email.trim()) return toast.error("Email is required so the patient can join HealthCircles");
    setBusy(true);
    try {
      const { data } = await api.post("/patients", {
        name, gender, age_type: ageType, age_value: ageValue, mobile, email: email.trim(),
      });
      if (data.linked) toast.success(`${data.name} linked to existing HealthCircles account`);
      else if (data.invited) toast.success(`${data.name} added — HealthCircles invite emailed`);
      else toast.success(`${data.name} added`);
      onCreated?.(data);
      onOpenChange(false);
      setName(""); setAgeValue(""); setMobile(""); setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add patient");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-[24px] max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] p-4 sm:p-6 flex flex-col gap-0"
        data-testid="add-patient-dialog"
      >
        <DialogHeader className="flex-none">
          <DialogTitle className="text-lg sm:text-xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>
            Add new patient
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 flex flex-col gap-4 min-h-0">
          <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-4">
            <div>
              <label className="field-label block mb-2">Name</label>
              <input data-testid="patient-name" required value={name} onChange={(e) => setName(e.target.value)} className="input-clean w-full" placeholder="Patient name" />
            </div>
            <div>
              <label className="field-label block mb-2">Gender</label>
              <div className="flex flex-wrap gap-2">
                {["Male", "Female", "Others"].map((g) => (
                  <button
                    key={g} type="button"
                    data-testid={`gender-${g.toLowerCase()}`}
                    onClick={() => setGender(g)}
                    className={`rounded-xl px-4 py-2 text-sm font-medium border transition-colors ${
                      gender === g ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white text-[#1F2A24] border-[#E8E6E1] hover:border-[#0A4C36]/40"
                    }`}
                  >{g}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="field-label block mb-2">Age</label>
              <div className="flex flex-wrap gap-2 items-stretch">
                <button type="button" onClick={() => setAgeType("DOB")} data-testid="age-dob" className={`rounded-xl px-4 py-2 text-sm font-medium border ${ageType === "DOB" ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1]"}`}>D.O.B</button>
                <button type="button" onClick={() => setAgeType("YRS")} data-testid="age-yrs" className={`rounded-xl px-4 py-2 text-sm font-medium border ${ageType === "YRS" ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1]"}`}>YRS</button>
                <input
                  data-testid="patient-age"
                  required
                  type={ageType === "DOB" ? "date" : "number"}
                  value={ageValue}
                  onChange={(e) => setAgeValue(e.target.value)}
                  className="input-clean flex-1 min-w-[100px]"
                  placeholder={ageType === "DOB" ? "" : "e.g. 38"}
                />
              </div>
            </div>
            <div>
              <label className="field-label block mb-2 inline-flex items-center gap-1.5">
                <Mail size={12} className="text-[#0A4C36]" /> Email <span className="text-[#B14A2A] font-semibold">*</span>
              </label>
              <input
                data-testid="patient-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-clean w-full"
                placeholder="patient@email.com"
              />
              <div className="text-[11px] text-[#8A9992] mt-1.5 leading-snug">
                We&rsquo;ll email them an invite to join HealthCircles so they can see appointments &amp; prescriptions.
                If they already have an account, we&rsquo;ll link automatically.
              </div>
            </div>
            <div>
              <label className="field-label block mb-2">Mobile number</label>
              <input data-testid="patient-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} className="input-clean w-full" placeholder="+91 …" />
            </div>
          </div>
          <div className="flex-none flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-[#EFECE5] -mx-4 sm:-mx-6 px-4 sm:px-6">
            <button type="button" onClick={() => onOpenChange(false)} className="btn-outline-emerald w-full sm:w-auto justify-center" data-testid="patient-cancel">
              Cancel
            </button>
            <button data-testid="patient-submit" disabled={busy} className="btn-emerald w-full sm:w-auto justify-center">
              {busy ? "Adding…" : "Add patient"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
